import { type ActionEvent, Controller } from "@hotwired/stimulus"

import { triggerGeolocationSearch } from "@/components/geolocation"
import { renderTurboStream } from "@/helpers/turbo"
import { debounceAsync, setInputValueWithEvents } from "@/helpers/utils"
import ModalController from "../modal_controller"

interface Location {
  locationType: "" | "geolocation" | "place" | "region" | "venue"
  maxDistance: string
  name: string
  slug: string
}

export default class extends Controller {
  static outlets = ["geolocation-modal"]
  declare geolocationModalOutlet: ModalController

  static targets = [
    "defaultResultsList",
    "hiddenInputLocationType",
    "hiddenInputMaxDistance",
    "hiddenInputSlug",
    "input",
    "popularDestinationsList",
    "recentLocationsList",
    "resetButton",
    "spasNearMe",
    "skipButton",
    "turboStreamResultsList"
  ]
  declare readonly defaultResultsListTargets: HTMLOListElement[]
  declare readonly hiddenInputLocationTypeTarget: HTMLInputElement
  declare readonly hiddenInputMaxDistanceTarget: HTMLInputElement
  declare readonly hiddenInputSlugTarget: HTMLInputElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly popularDestinationsListTarget: HTMLOListElement
  declare readonly recentLocationsListTarget: HTMLOListElement
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly hasResetButtonTarget: boolean
  declare readonly spasNearMeTarget: HTMLOListElement
  declare readonly skipButtonTarget: HTMLButtonElement
  declare readonly hasSkipButtonTarget: boolean
  declare readonly turboStreamResultsListTarget: HTMLOListElement

  static values = {
    endpoint: String,
    resultsId: String,
    selectedLocation: Object
  }
  declare readonly endpointValue: string
  declare readonly resultsIdValue: string
  declare selectedLocationValue: Location

  // For Bloomreach `search` event.
  typedSearchTerm = ""

  private lookupAbort = new window.AbortController()

  initialize(): void {
    this.lookup = debounceAsync(this.lookup.bind(this), 250)
  }

  connect(): void {
    // Input disabled by default until controller loaded.
    this.inputTarget.disabled = false
  }

  clearLocation(): void {
    this.typedSearchTerm = ""
    this.selectedLocationValue = this.emptyLocation
    this.setInputValue("")
  }

  selectedLocationValueChanged(): void {
    this.hiddenInputLocationTypeTarget.value = this.selectedLocationValue.locationType
    this.hiddenInputMaxDistanceTarget.value = this.selectedLocationValue.maxDistance
    this.hiddenInputSlugTarget.value = this.selectedLocationValue.slug

    // Only send max distance when it has a value.
    this.hiddenInputMaxDistanceTarget.disabled = !this.selectedLocationValue.maxDistance
    this.updateSkipAndResetButtons()
  }

  async lookup({ target }: ActionEvent): Promise<void> {
    const input = target as HTMLInputElement
    const query = input.value.trim()

    this.typedSearchTerm = query

    // Any lookup should reset currently selected location.
    this.selectedLocationValue = this.emptyLocation

    if (query) {
      await this.renderResults(query)
    } else {
      this.clearResultsAndShowDefaults()
    }
  }

  onInputFocus(): void {
    this.inputTarget.select()

    if (this.isModal) {
      this.popularDestinationsListTarget.hidden =
        !!this.turboStreamResultsListTarget.children.length
      this.recentLocationsListTarget.hidden = true
    }
  }

  onInputBlur({ relatedTarget }: FocusEvent): void {
    const controllerHasFocus = this.element.contains(relatedTarget as Node)

    // If element receiving focus is outside controller then user has not made a selection,
    // so clear input.
    if (!this.hasSelectedLocation && !controllerHasFocus) {
      this.clearLocation()
      this.clearResultsAndShowDefaults()
    } else {
      this.setInputValue(this.selectedLocationValue.name)
    }
  }

  // Keep input focused so click on a result registers before blur
  onResultMousedown(e: Event): void {
    e.preventDefault()
  }

  async selectResult({ params: { result } }: ActionEvent): Promise<void> {
    if (result.locationType === "geolocation") {
      try {
        const coords = await triggerGeolocationSearch()
        if (coords) {
          this.selectedLocationValue = {
            ...result,
            slug: `${coords.lat},${coords.lng}`
          }
        } else {
          this.selectedLocationValue = this.emptyLocation
        }
      } catch {
        this.selectedLocationValue = this.emptyLocation
        this.geolocationModalOutlet.open()
      }
    } else {
      this.selectedLocationValue = result
    }

    this.setInputValue(this.selectedLocationValue.name)
  }

  setInputValue(value: string): void {
    // Set value with `change` only, because `input` would trigger another lookup fetch.
    setInputValueWithEvents(this.inputTarget, value, ["change"])
  }

  updateSkipAndResetButtons(): void {
    if (this.hasResetButtonTarget && this.hasSkipButtonTarget) {
      const isSelected = !!this.inputTarget.value

      this.resetButtonTarget.hidden = !isSelected
      this.skipButtonTarget.hidden = isSelected
    }
  }

  private clearResultsAndShowDefaults(): void {
    this.turboStreamResultsListTarget.innerHTML = ""
    this.spasNearMeTarget.hidden = false

    if (this.isModal) {
      const inputHasFocus = this.inputTarget === document.activeElement
      this.popularDestinationsListTarget.hidden = !inputHasFocus
      this.recentLocationsListTarget.hidden = inputHasFocus
    } else {
      this.defaultResultsListTargets.forEach((target) => (target.hidden = false))
    }
  }

  private get emptyLocation(): Location {
    return {
      locationType: "",
      maxDistance: "",
      name: "",
      slug: ""
    }
  }

  private get hasSelectedLocation(): boolean {
    return !!this.selectedLocationValue.locationType
  }

  private get isModal(): boolean {
    return this.hasSkipButtonTarget
  }

  private async renderResults(query: string): Promise<void> {
    const url = new URL(this.endpointValue, window.location.origin)
    const params = new URLSearchParams({
      q: query,
      results_id: this.resultsIdValue
    })
    url.search = params.toString()

    // Abort any previous query and reset controller.
    this.lookupAbort.abort()
    this.lookupAbort = new window.AbortController()

    await renderTurboStream(url.toString(), {
      signal: this.lookupAbort.signal
    })

    // Hide defaults
    this.defaultResultsListTargets.forEach((target) => (target.hidden = true))
    this.spasNearMeTarget.hidden = true
  }
}
